import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Phone, Home, ClipboardCheck, CheckCircle, ArrowRight, Shield, 
  ThumbsUp, Award, Calendar, Clock, FileText, Star
} from 'lucide-react';

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#003366] text-white overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Four Simple Steps to
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Home Confidence</span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            From first call to Home Profile in hand — <span className="text-[#CFB973] font-semibold">no surprises, no pressure</span>, just clarity.
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-[#E8EEF3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Clock className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">48-Hour</p>
              <p className="text-[#003366]/60 text-xs">Turnaround</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <ThumbsUp className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">No Pressure</p>
              <p className="text-[#003366]/60 text-xs">No Upselling</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">100% Satisfaction</p>
              <p className="text-[#003366]/60 text-xs">Guarantee</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps - Visual Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Your Journey with Valenrock</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We've designed a process that respects your time and delivers real value. Here's exactly what happens.
            </p>
          </div>

          {/* Step 1 */}
          <div className="flex flex-col lg:flex-row items-center gap-8 mb-16">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center shadow-lg">
                  <span className="text-[#003366] font-bold text-xl">1</span>
                </div>
                <h3 className="text-2xl font-bold text-[#003366] mb-4 ml-6">Connect with Us</h3>
                <p className="text-gray-600 mb-4">
                  Reach out by phone or form. We'll have a brief, no-pressure conversation to understand:
                </p>
                <ul className="space-y-3">
                  {['Your home (age, size, type)', 'Any specific concerns', 'Your goals and timeline', 'Basic questions about our process'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#003366]" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-[#003366] font-semibold mt-4 flex items-center gap-2">
                  <Clock size={16} /> Takes about 10 minutes
                </p>
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2 flex justify-center">
              <div className="w-40 h-40 rounded-full bg-[#003366] flex items-center justify-center shadow-2xl">
                <Phone className="w-20 h-20 text-[#CFB973]" />
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col lg:flex-row items-center gap-8 mb-16">
            <div className="lg:w-1/2 flex justify-center">
              <div className="w-40 h-40 rounded-full bg-[#003366] flex items-center justify-center shadow-2xl">
                <Calendar className="w-20 h-20 text-[#CFB973]" />
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center shadow-lg">
                  <span className="text-[#003366] font-bold text-xl">2</span>
                </div>
                <h3 className="text-2xl font-bold text-[#003366] mb-4 ml-6">Schedule Your Assessment</h3>
                <p className="text-gray-600 mb-4">
                  Pick a time that works for you. We'll confirm pricing based on your home's size and send a reminder.
                </p>
                <div className="bg-white rounded-xl p-4 border-2 border-[#CFB973]">
                  <p className="text-sm text-gray-600 mb-2">Assessment Pricing:</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Single-Family Home</span>
                      <span className="font-bold text-[#003366]">From $249</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Multi-Unit / Large Home</span>
                      <span className="font-bold text-[#003366]">From $449</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-[#003366] font-semibold mt-4 flex items-center gap-2">
                  <Clock size={16} /> Usually scheduled within 3-5 days
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col lg:flex-row items-center gap-8 mb-16">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center shadow-lg">
                  <span className="text-[#003366] font-bold text-xl">3</span>
                </div>
                <h3 className="text-2xl font-bold text-[#003366] mb-4 ml-6">The Home Walkthrough</h3>
                <p className="text-gray-600 mb-4">
                  We walk through your home together, reviewing every major system and space:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {['Roof & Exterior', 'HVAC & Electrical', 'Plumbing', 'Kitchen & Baths', 'Windows & Doors', 'Basement & Attic', 'Appliances', 'Safety Items'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#CFB973]" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#003366] font-semibold mt-4 flex items-center gap-2">
                  <Clock size={16} /> 1-2 hours depending on home size
                </p>
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2 flex justify-center">
              <div className="w-40 h-40 rounded-full bg-[#003366] flex items-center justify-center shadow-2xl">
                <Home className="w-20 h-20 text-[#CFB973]" />
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="lg:w-1/2 flex justify-center">
              <div className="w-40 h-40 rounded-full bg-[#003366] flex items-center justify-center shadow-2xl">
                <ClipboardCheck className="w-20 h-20 text-[#CFB973]" />
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center shadow-lg">
                  <span className="text-[#003366] font-bold text-xl">4</span>
                </div>
                <h3 className="text-2xl font-bold text-[#003366] mb-4 ml-6">Receive Your Home Profile</h3>
                <p className="text-gray-600 mb-4">
                  Within 48 hours, you'll receive your comprehensive Home Profile including:
                </p>
                <ul className="space-y-3">
                  {['Overall Home Score with category breakdown', 'Priority issues ranked by urgency', 'Appliance inventory (brand, model, year)', 'Maintenance timeline & recommendations', 'Photo documentation'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#003366]" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-[#003366] font-semibold mt-4 flex items-center gap-2">
                  <Clock size={16} /> Delivered within 48 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Then What?</h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Your Home Profile is yours. Use it however you'd like — there's no obligation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4 shadow-lg">
                <FileText className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">Keep It for Reference</h3>
              <p className="text-white/70 text-sm">Use it to plan your own maintenance or share with other contractors.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Calendar className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">Schedule Services</h3>
              <p className="text-white/70 text-sm">Let us handle repairs, maintenance, or improvements on your timeline.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Star className="w-8 h-8 text-[#003366]" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">Priority Client Status</h3>
              <p className="text-white/70 text-sm">Assessed clients get faster scheduling and priority support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tagline */}
      <section className="py-10 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl md:text-2xl text-[#003366] font-medium italic">
            "We're not an insurance policy. We're your home's reassurance plan."
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Waitlist Notice */}
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-8 max-w-2xl mx-auto">
            <p className="text-amber-800 font-medium">
              <span className="block sm:inline">Currently at capacity.</span>{' '}
              <span className="block sm:inline">Join our waitlist — scheduling opens soon.</span>
            </p>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">
            Ready to Take the First Step?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Join the waitlist now. We'll contact you when scheduling opens.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-12 py-7 text-xl shadow-xl">
                Join the Waitlist
              </Button>
            </Link>
            <a href="tel:5513799774">
              <Button size="lg" className="bg-[#003366] hover:bg-[#003366]/90 text-white font-semibold px-10 py-7 text-lg">
                <Phone className="mr-2" size={20} />
                (551) 379-9774
              </Button>
            </a>
          </div>
          <p className="text-gray-500 text-sm mt-6">Remodeling services still available • Northern & Central NJ</p>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
